import { List, Sun, Moon, SignOut, SealCheck, Gauge, Question } from "@phosphor-icons/react";
import { NotificationBell } from "@/features/notifications/components/NotificationBell";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { useBusinessContext } from "@/hooks/useBusinessContext";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useTheme } from "@/components/theme-provider";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { useUIStore } from "@/stores/uiStore";
import { isFeatureEnabled } from "@/lib/flags";
import { toast } from "@/lib/toast";
import { formatDisplayName, getNameInitials } from "@/lib/profileName";

export function AppHeader() {
  const { theme, setTheme } = useTheme();
  const { user, isLoggingOut, signOut } = useCurrentUser();
  const { query: businessContextQuery, isComplete: profileVerified } = useBusinessContext();
  const drawerOpen = useUIStore((s) => s.drawerOpen);
  const toggleDrawer = useUIStore((s) => s.toggleDrawer);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const authEnabled = isFeatureEnabled("AUTH_ENABLED");
  const displayName = businessContextQuery.data
    ? formatDisplayName(
        businessContextQuery.data.display_name,
        businessContextQuery.data.honorific_prefix,
      )
    : (user?.name ?? "");
  const initialsSource = businessContextQuery.data
    ? { name: businessContextQuery.data.display_name, prefix: businessContextQuery.data.honorific_prefix }
    : { name: user?.name ?? "", prefix: null };
  const specialty = businessContextQuery.data?.niche?.trim() || user?.specialty || "";

  const toggleTheme = () => {
    setTheme(theme === "dark" ? "light" : "dark");
  };

  const handleSignOut = async () => {
    const { error } = await signOut();
    if (error) {
      toast.error("Erro ao sair", { description: (error as Error).message });
      return;
    }
    queryClient.clear();
    navigate("/login", { replace: true });
  };

  return (
    <header className="h-16 px-4 lg:px-6 flex items-center justify-between bg-background/60 backdrop-blur-xl">
      {/* Mobile hamburger — opens the sidebar drawer at < 1024px. */}
      <Button
        variant="ghost"
        size="icon"
        onClick={toggleDrawer}
        aria-label="Abrir menu"
        aria-expanded={drawerOpen}
        aria-controls="app-sidebar-drawer"
        className="lg:hidden h-12 w-12"
      >
        <List weight="regular" aria-hidden="true" className="w-6 h-6" />
      </Button>

      {/* Right side */}
      <div className="flex items-center gap-2 lg:gap-4 ml-auto">
        {/* Theme Toggle */}
        <Button
          variant="ghost"
          size="icon"
          onClick={toggleTheme}
          aria-label={theme === "dark" ? "Ativar tema claro" : "Ativar tema escuro"}
          className="h-12 w-12 lg:h-10 lg:w-10"
        >
          {theme === "dark" ? (
            <Sun weight="regular" aria-hidden="true" className="w-5 h-5 text-muted-foreground hover:text-foreground transition-colors" />
          ) : (
            <Moon weight="regular" aria-hidden="true" className="w-5 h-5 text-muted-foreground hover:text-foreground transition-colors" />
          )}
        </Button>

        {/* Notifications */}
        <NotificationBell />

        {/* User */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              className="flex items-center gap-3 rounded-full p-1 hover:bg-muted/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              aria-label="Menu do usuário"
            >
              <div className="text-right hidden sm:flex sm:flex-col sm:items-end">
                <p className="text-sm font-medium text-foreground">{displayName}</p>
                <p className="text-xs text-muted-foreground">{specialty}</p>
              </div>
              <div className="relative">
                <Avatar className="w-9 h-9 border-2 border-primary/20">
                  <AvatarImage src="" alt="" />
                  <AvatarFallback className="bg-primary/10 text-primary text-sm font-medium">
                    {getNameInitials(initialsSource.name, initialsSource.prefix)}
                  </AvatarFallback>
                </Avatar>
                {profileVerified ? (
                  <SealCheck
                    weight="fill"
                    className="absolute -bottom-0.5 -right-0.5 w-4 h-4 text-primary bg-background rounded-full"
                    aria-label="Perfil verificado"
                  />
                ) : null}
              </div>
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-48">
            <DropdownMenuLabel>{user?.email ?? "Conta"}</DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem onSelect={() => navigate("/perfil")}>Perfil</DropdownMenuItem>
            <DropdownMenuItem onSelect={() => navigate("/uso")}>
              <Gauge weight="regular" className="mr-2 h-4 w-4" aria-hidden="true" />
              Uso & Custos
            </DropdownMenuItem>
            <DropdownMenuItem onSelect={() => navigate("/ajuda")}>
              <Question weight="regular" className="mr-2 h-4 w-4" aria-hidden="true" />
              Ajuda
            </DropdownMenuItem>
            <DropdownMenuItem onSelect={() => navigate("/configuracoes")}>
              Configurações
            </DropdownMenuItem>
            {authEnabled && (
              <>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onSelect={(e) => {
                    e.preventDefault();
                    void handleSignOut();
                  }}
                  disabled={isLoggingOut}
                  className="text-destructive focus:text-destructive"
                >
                  <SignOut weight="regular" className="mr-2 h-4 w-4" aria-hidden="true" />
                  {isLoggingOut ? "Saindo..." : "Sair"}
                </DropdownMenuItem>
              </>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  );
}
